import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { ISearchResponse, ResultObjectType } from '@cbgms/api'
import { UseQueryResult, useQueries, useQuery } from '@cbgms/base/http/use-query'
import { useFeature } from '@cbgms/base/feature-flags'
import { handleSearchLimit } from 'app/search/state/actions'

export const SEARCH = 'SEARCH'
export const SEARCH_PARTS = 'SEARCH_PARTS'

export const emptySearch: ISearchResponse = { Errors: [], Filters: [], LimitedResultCount: 0, Results: [], TotalResultCount: 0 }
// eslint-disable-next-line carsys-custom/typescript-prefer-interface-extend-over-intersection
export type IOutput = UseQueryResult<ISearchResponse, null> & { isPartsLoading: boolean }

const recombineResults = (queries: [UseQueryResult<ISearchResponse>, UseQueryResult<ISearchResponse>]): IOutput => {
  const regular = queries[0]
  const parts = queries[1]
  // Can't properly spread 'error' and 'failureReason' properties, have to cast to IOutput:
  const result: IOutput = { ...regular, data: { ...emptySearch }, isPartsLoading: false } as any as IOutput
  result.data!.Errors = [...(regular?.data?.Errors || []), ...(parts?.data?.Errors || [])]
  result.data!.Results = [...(regular?.data?.Results || []), ...(parts?.data?.Results || [])]

  result.data!.Filters = [...(parts?.data?.Filters || [])] // this can effectively only come form the product result
  result.data!.LimitedResultCount = (regular?.data?.LimitedResultCount || 0) + (parts?.data?.LimitedResultCount || 0)
  result.data!.TotalResultCount = (regular?.data?.TotalResultCount || 0) + (parts?.data?.TotalResultCount || 0)
  result.isPartsLoading = parts.isFetching

  if (!result.data!.Results.length && parts.isFetching) {
    // reset search data if regular search did not procude results and parts is still fetching
    result.data = undefined
    result.isLoading = true
    result.isInitialLoading = true
  }

  return result
}

const ThirthySeconds = 30 * 1000

export const useQueryResultTypes = (
  enabled: (q: string) => boolean,
  query: string,
  resultTypes: ResultObjectType[],
  limit = 9999,
  excludeFromRegularSearch?: ResultObjectType[]
): IOutput => {
  const dispatch = useDispatch()
  const featureFlagValue = useFeature('b2b-search-optimization')

  // Always compute these values
  const hasPartSearch = useMemo(() => resultTypes.includes(ResultObjectType.Parts), [resultTypes])
  const defaultExclusions = useMemo(() => (featureFlagValue ? [ResultObjectType.TyreSize] : []), [featureFlagValue])

  // Single query for v2/dashboard (when excludeFromRegularSearch is provided)
  const shouldUseSingleQuery = excludeFromRegularSearch !== undefined
  const searchTypes = useMemo(() => {
    return shouldUseSingleQuery
      ? resultTypes.filter(r => !excludeFromRegularSearch.includes(r))
      : resultTypes.filter(r => r !== ResultObjectType.Parts && !defaultExclusions.includes(r))
  }, [resultTypes, excludeFromRegularSearch, shouldUseSingleQuery, defaultExclusions])

  const singleSearchQuery = useQuery({
    queryKey: [SEARCH, query, ...Object.values(searchTypes), shouldUseSingleQuery ? 'single' : 'regular'],
    cacheTime: ThirthySeconds,
    staleTime: ThirthySeconds,
    enabled: shouldUseSingleQuery && enabled(query),
    queryFn: async () => {
      if (!searchTypes.length) {
        return emptySearch
      }
      // Only use external parts search when Parts are included in the search types
      const includesParts = searchTypes.includes(ResultObjectType.Parts)
      const { Data } = await dispatch(handleSearchLimit(query, limit, includesParts, searchTypes))
      return Data
    }
  })

  // Dual queries for v1 (when excludeFromRegularSearch is undefined)
  const queries = useQueries({
    queries: [
      {
        queryKey: [SEARCH, query, ...Object.values(resultTypes)],
        cacheTime: ThirthySeconds,
        staleTime: ThirthySeconds,
        enabled: !shouldUseSingleQuery && enabled(query),
        queryFn: async () => {
          const regularSearchTypes = resultTypes.filter(r => r !== ResultObjectType.Parts && !defaultExclusions.includes(r))

          if (!regularSearchTypes.length) {
            return emptySearch
          }
          const { Data } = await dispatch(handleSearchLimit(query, limit, false, regularSearchTypes))
          return Data
        }
      },
      {
        queryKey: [SEARCH, SEARCH_PARTS, query, ResultObjectType.Parts],
        cacheTime: ThirthySeconds,
        staleTime: ThirthySeconds,
        enabled: !shouldUseSingleQuery && hasPartSearch && enabled(query),
        queryFn: async () => {
          if (!resultTypes.length) {
            return emptySearch
          }
          const { Data } = await dispatch(handleSearchLimit(query, limit, true, [ResultObjectType.Parts]))
          return Data
        }
      }
    ]
  })

  const singleQueryResult = useMemo(
    () => ({ ...singleSearchQuery, isPartsLoading: singleSearchQuery.isFetching } as IOutput),
    [singleSearchQuery]
  )

  const dualQueryResult = useMemo(
    () => recombineResults(queries),
    [
      queries[0].data,
      queries[1].data,
      queries[0].isInitialLoading,
      queries[1].isInitialLoading,
      queries[0].isLoading,
      queries[1].isLoading,
      queries[0].isFetching,
      queries[1].isFetching
    ]
  )

  return shouldUseSingleQuery ? singleQueryResult : dualQueryResult
}
